import { useState, useCallback, useEffect, useRef } from 'react';
import './JournalTableOfContents.css';

export interface TOCItem {
    id: string;
    title: string;
    type: 'note' | 'pdf' | 'youtube' | 'quiz' | 'slide-deck' | 'streaming-note' | 'todo';
}

interface JournalTableOfContentsProps {
    items: TOCItem[];
    activeItemId: string | null;
    onItemClick: (id: string) => void;
    /** Minimum items required to show TOC (default: 3) */
    minItems?: number;
}

export const JournalTableOfContents: React.FC<JournalTableOfContentsProps> = ({
    items,
    activeItemId,
    onItemClick,
    minItems = 3,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hoveredLineIndex, setHoveredLineIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Handle click outside to close
    useEffect(() => {
        if (!isExpanded) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsExpanded(false);
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isExpanded]);

    // Cleanup hover timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const handleItemClick = useCallback((id: string) => {
        onItemClick(id);
    }, [onItemClick]);

    const handleClick = useCallback(() => {
        setIsExpanded(true);
        setHoveredLineIndex(null);
    }, []);

    // Hover to expand (with small delay for intentional hovers)
    const handleMouseEnter = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            setIsExpanded(true);
        }, 300); // 300ms delay to avoid accidental triggers
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        // Close panel when mouse leaves the entire TOC area
        setIsExpanded(false);
    }, []);

    const handleClose = useCallback(() => {
        setIsExpanded(false);
    }, []);

    // Don't render if not enough items (AFTER all hooks)
    if (items.length < minItems) {
        return null;
    }

    return (
        <nav
            className="journal-toc"
            aria-label="Table of contents"
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {!isExpanded ? (
                /* Collapsed: Abstract lines */
                <div
                    className="toc-collapsed"
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                    aria-label="Open table of contents"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                        }
                    }}
                >
                    {items.map((item, index) => (
                        <div
                            key={item.id}
                            className={`toc-line ${activeItemId === item.id ? 'active' : ''}`}
                            data-type={item.type}
                            onMouseEnter={() => setHoveredLineIndex(index)}
                            onMouseLeave={() => setHoveredLineIndex(null)}
                        >
                            {/* Tooltip on hover */}
                            {hoveredLineIndex === index && (
                                <div className="toc-tooltip">
                                    {truncateTitle(item.title, 22)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                /* Expanded: Panel with titles */
                <div className="toc-panel">
                    <div className="toc-panel-header">
                        <span className="toc-panel-title">Contents</span>
                        <button
                            className="toc-close-btn"
                            onClick={handleClose}
                            aria-label="Close table of contents"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    {items.map((item) => (
                        <button
                            key={item.id}
                            className={`toc-item ${activeItemId === item.id ? 'active' : ''}`}
                            onClick={() => handleItemClick(item.id)}
                        >
                            <span className="toc-item-indicator" />
                            <span className="toc-item-title">
                                {truncateTitle(item.title, 24)}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </nav>
    );
};

/** Truncate title with ellipsis */
function truncateTitle(title: string, maxLength: number): string {
    if (!title) return 'Untitled';
    if (title.length <= maxLength) return title;
    return title.slice(0, maxLength - 1).trim() + '…';
}

export default JournalTableOfContents;
